// src/lib/managers/contact/ContactManager.ts

import { EventBus } from "../../contexts";
import { filterNoReplyContactsForQuery, logger, LOGS } from "../../utils";
import { isNoReplyAddress } from "../../utils/contactFilter/contactFilter";
import { ApiManager } from "../api/ApiManager";
import { SearchContactsResponseDTO } from "../api/types/apiResponse";
import { DataManager } from "../dataManager/DataManager";
import type { ContactPerson } from "../dataManager/IDataApi";
import { MailRecord } from "../mail/MailRecord";
import {
    exactFirst,
    isEmailAddress,
    matchesName,
    nameTokens,
    resemblesName,
} from "./contactName";
import { contactSearchResultsToEmails } from "./mappers/contactSearch.mapper";
import {
    BestContactResult,
    ContactCandidate,
    ResolveContactsOptions,
    ResolveContactsResult,
} from "./types/contact.types";

/**
 * Écart de score sous lequel deux candidats sont jugés aussi pertinents l'un que l'autre
 * (`ambiguous`). Provisoire : à régler sur les scores réels écrits dans les logs (AB#1634).
 */
const AMBIGUITY_GAP = 0.05;

/** Contacts ressemblants proposés pour un nom introuvable, au plus. */
const SUGGESTIONS_MAX = 3;

/** Résultats demandés au RAG par défaut. */
const RAG_DEFAULT_TOP_K = 20;

/**
 * Délai par défaut laissé au RAG avant le repli, en millisecondes, pour la recherche classique
 * (`resolveContacts` : champ destinataire, vues). Hérité du pilote AB#1634. Le contrat v1 de
 * l'assistant le passe aussi à `resolveBestContact` : il garde ses 2 s.
 */
export const RAG_DEFAULT_TIMEOUT_MS = 2000;

/**
 * Pour l'assistant par étapes (`resolveBestContact` sans `timeoutMs`), le RAG passe en priorité : on
 * attend sa réponse, seule une vraie panne fait passer au carnet. Puis le carnet de la boîte, sous le
 * même délai (jeton du fournisseur compris) : une recherche de contact ne bloque jamais un tour.
 */
const ASSISTANT_RAG_TIMEOUT_MS = 10_000;
const ASSISTANT_BOOK_TIMEOUT_MS = 10_000;
/**
 * Le carnet n'attend pas la fin du RAG : s'il n'a pas répondu au bout de ce délai, le carnet part en même
 * temps. Le RAG garde la priorité ; au pire, 10 s (RAG) au lieu de 10 s + 10 s, l'un après l'autre (audit
 * de performance du 15/10, P3-04).
 */
const ASSISTANT_BOOK_HEAD_START_MS = 2_000;

/** Une recherche qui n'a pas répondu dans son délai : reconnue par son type, jamais par son texte. */
class ContactTimeoutError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = "ContactTimeoutError";
        Object.setPrototypeOf(this, ContactTimeoutError.prototype);
    }
}

/** Pourquoi le RAG n'a pas donné de contact : écrit au journal, pour qu'un RAG muet se voie. */
type RagFallbackReason = "vide" | "aucun_compatible" | "erreur" | "delai";

/**
 * @class ContactManager
 * Point d'orchestration UNIQUE de la recherche de contact. Méthodes :
 *  - `resolveContacts` (public) : recherche RAG-first + repli legacy — LE point d'entrée des appelants.
 *  - `searchContacts`  (public) : appel RAG brut (GET v1/contacts/search) → DTO scoré.
 *  - `_searchLegacy`   (privé)  : recherche historique = carnet Graph/Google ∪ scan du cache mémoire des mails.
 * ═══════════════════════════════════════════════════════════════════════════
 * Pilote AB#1634 (RAG pour la recherche de contact). L'activation est portée CÔTÉ BACKEND par la
 * variable d'env `RAG_CONTACT_SEARCH_ENABLED` — il n'y a NI flag NI bootstrap côté SDK. Flag OFF →
 * l'endpoint répond `results: []` → `resolveContacts` retombe sur `searchLegacy` → comportement
 * identique à l'historique (au prix d'un aller-retour réseau vide). Bascule sans redéploiement du SDK.
 *
 * Dépendances : `apiManager` (RAG HTTP), `dataManager` (Graph/Google), `mailRecord` (scan du cache
 * mémoire des mails, partagé avec MailManager). `MailManager.searchContact` délègue ici
 * (`resolveContacts`) pour préserver l'API publique du front.
 */
export class ContactManager {
    // #region Properties

    private eventBus: EventBus;

    private apiManager: ApiManager;
    private dataManager: DataManager;
    private mailRecord: MailRecord;

    // AB#1725 — garde de session : un seul déclenchement de l'amorçage RAG par démarrage
    // (le backend est de toute façon idempotent via le flag de profil).
    private contactRagInitRequested = false;

    private readonly hash: number = Math.round(Math.random() * 100);

    // #endregion Properties

    /**
     * @constructor
     * Initialise un nouveau ContactManager avec le bus d'événements.
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {EventBus} eventBus - Bus d'événements pour la communication
     * @output {void}
     */
    constructor(
        eventBus: EventBus,
        apiManager: ApiManager,
        dataManager: DataManager,
        mailRecord: MailRecord,
    ) {
        logger.info([LOGS.ContactManager], `ContactManager(${this.hash})`);

        this.eventBus = eventBus;
        this.apiManager = apiManager;
        this.dataManager = dataManager;
        this.mailRecord = mailRecord;
    }

    // #region Initialisation and cleanup

    /**
     * @method cleanup
     * Nettoie les références aux autres managers.
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.ContactManager,
            "ContactManager._cleanup() - Starting cleanup process",
        );

        // Libellé "cleanup" et non "Phase N" pour ne pas confondre avec les phases de startup.
        try {
            logger.info(
                LOGS.ContactManager,
                "  Cleanup: resetting session guard",
            );

            this.contactRagInitRequested = false;
        } catch (error) {
            logger.error(
                LOGS.ContactManager,
                `ContactManager._cleanup() - Error while resetting session guard: ${error}`,
            );
        }

        logger.info(
            LOGS.ContactManager,
            "ContactManager._cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents() {
        // AB#1725 — Amorçage one-shot du carnet de contacts RAG, déclenché en fin de démarrage.
        // `services-ready` est émis à l'affichage du Dashboard : pour un nouvel utilisateur, c'est
        // juste après `initial-analysis-completed` (ou le timeout 3 min) ; pour un utilisateur
        // existant, après le délai logo — dans les deux cas HORS de la fenêtre de charge du startup.
        // Note : un double appel de _registerEvents sans _cleanup intermédiaire
        // empilerait le handler deux fois (mitt accepte les doublons) — sans conséquence, la garde
        // de session `contactRagInitRequested` neutralise le double déclenchement de l'appel API.
        this.eventBus.on("services-ready", this._handleServicesReady);
    }

    // Référence stable (propriété fléchée) pour permettre le off() au cleanup.
    private _handleServicesReady = (): void => {
        void this._initializeContactRagOnce();
    };

    /**
     * AB#1725 — Déclenche l'amorçage du carnet RAG côté backend (POST v1/contacts/rag-initialize).
     * Best-effort TOTAL : ne rejette jamais. Un échec laisse le flag backend à `false` → nouvelle
     * tentative au prochain démarrage (pas de retry intra-session). Le backend est idempotent :
     * carnet déjà amorcé → no-op (`alreadyInitialized: true`), sans appel Graph/RAG.
     */
    private async _initializeContactRagOnce(): Promise<void> {
        if (this.contactRagInitRequested) return;
        this.contactRagInitRequested = true;

        try {
            const startTime = performance.now();
            const result = await this.apiManager.initializeContactRag();
            logger.info(
                [LOGS.ContactManager, LOGS.AB1725],
                `[STARTUP-TIMING] initializeContactRag : initialized=${result.initialized}, alreadyInitialized=${result.alreadyInitialized} (+${Math.round(performance.now() - startTime)} ms)`,
            );
        } catch (error) {
            logger.warn(
                [LOGS.ContactManager, LOGS.AB1725],
                `initializeContactRag failed (best-effort, retenté au prochain démarrage) : ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion Event Handlers

    // #region Public Methods

    /**
     * Recherche hybride de contacts (sémantique + typo-tolérante) via l'Api-Backend (RAG brut).
     * ═══════════════════════════════════════════════════════════════════════════
     * `results` est déjà trié par pertinence décroissante côté serveur — ne pas re-trier.
     * Une `query` vide est court-circuitée par l'ApiManager (aucun appel réseau).
     *
     * @param query - Texte libre (nom, fragment d'email, langage naturel)
     * @param topK  - Nombre max de résultats souhaités (clampé à [1, 50] par l'ApiManager)
     * @returns Promise<SearchContactsResponseDTO> - { query, results, total }
     */
    public async searchContacts(
        query: string,
        topK: number = 10,
    ): Promise<SearchContactsResponseDTO> {
        logger.info(LOGS.ContactManager, `searchContacts("${query}", ${topK})`);

        try {
            const result: SearchContactsResponseDTO =
                await this.apiManager.searchContacts(query, topK);

            logger.info(LOGS.ContactManager, "Contacts search result", result);
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error searching contacts: ${errorMessage}`;
            logger.error(LOGS.ContactManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * @method searchLegacy
     * Recherche de contacts historique : union du carnet Graph/Google (via DataManager) et du scan
     * du cache mémoire des mails (via MailRecord), dédupliquée en minuscules.
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} searchString - Terme de recherche
     * @output {Promise<string[]>} Liste des contacts trouvés (minuscules, dédupliqués)
     */
    private async _searchLegacy(searchString: string): Promise<string[]> {
        // Lance l'appel réseau SANS await
        const graphContactPromise = this.dataManager
            .searchContact(searchString)
            .catch((error) => {
                logger.error(
                    LOGS.ContactManager,
                    "Erreur in searchLegacy during call to this.dataManager.searchContact:",
                    error,
                );
                return []; // Valeur par défaut
            });

        // Exécute le traitement local pendant que le réseau travaille (scan du cache mémoire des mails)
        const mailRecordContactResult =
            this.mailRecord.searchEmailsByString(searchString);
        // Attend uniquement le temps restant du réseau
        const graphContactResult = await graphContactPromise;

        // Combine les deux tableaux et élimine les doublons avec un Set
        const uniqueContacts = new Set(
            [...mailRecordContactResult, ...graphContactResult].map((c) =>
                c.toLowerCase(),
            ),
        );

        return Array.from(uniqueContacts);
    }

    /**
     * Recherche de contacts par RAG (AB#1634) avec repli systématique sur la recherche legacy.
     * ═══════════════════════════════════════════════════════════════════════════
     * Branche RAG (résultat non vide) → `source: 'rag'`, union de deux portions :
     *   - portion RAG : gardée telle quelle (vettée par la pertinence sémantique du serveur) ;
     *   - portion scan local (`MailRecord.searchEmailsByString`, simple match de sous-chaîne) :
     *     FILTRÉE no-reply via `filterNoReplyContactsForQuery` (AB#1744). Sans ça, un `no-reply@…`
     *     capté par sous-chaîne fuiterait : les sites sautent le filtre pour `source: 'rag'`, or cette
     *     portion locale n'est PAS vettée par un score → il faut lui ré-appliquer l'heuristique AB#1744.
     *
     * Repli `source: 'legacy'` : RAG vide, erreur/timeout, ou dépendances absentes. Best-effort TOTAL —
     * `resolveContacts` ne rejette JAMAIS : un échec du legacy renvoie `{ emails: [], source: 'legacy' }`,
     * et le site restaure alors le terme brut via sa substitution `[] → [term]` (filtre expéditeur préservé).
     *
     * ⚠️ PILOTE — le score RAG n'est PAS seuillé : `minScore` vaut 0 par défaut, on prend donc TOUT ce que
     * le RAG renvoie (recall maximal). C'est volontaire pour observer la distribution des scores en
     * production ; à CALIBRER (probablement côté service RAG) APRÈS des tests en conditions réelles.
     *
     * @returns `{ emails, source }` — `source` dicte le post-traitement no-reply au site.
     */
    public async resolveContacts(
        searchString: string,
        opts?: ResolveContactsOptions,
    ): Promise<ResolveContactsResult> {
        const topK = opts?.topK ?? RAG_DEFAULT_TOP_K;
        const minScore = opts?.minScore ?? 0;
        const timeoutMs = opts?.timeoutMs ?? RAG_DEFAULT_TIMEOUT_MS;

        try {
            // Tentative RAG sous garde de timeout.
            const ragResponse = await this._searchContactsWithin(
                searchString,
                topK,
                timeoutMs,
                "resolveContacts",
            );

            const ragEmails = contactSearchResultsToEmails(ragResponse, {
                minScore,
            });

            // RAG non vide → union : RAG d'abord (vetté par le score), puis scan local FILTRÉ no-reply (AB#1744).
            if (ragEmails.length > 0) {
                const localEmails = filterNoReplyContactsForQuery(
                    this.mailRecord
                        .searchEmailsByString(searchString)
                        .map((email) => email.toLowerCase()),
                    searchString,
                );
                const unionEmails = new Set<string>(ragEmails);
                localEmails.forEach((email) => unionEmails.add(email));
                return { emails: [...unionEmails], source: "rag" };
            }
        } catch (error) {
            // Best-effort : échec/timeout RAG → on loggue (aide à diagnostiquer « le RAG ne remonte rien »)
            // puis on retombe sur le legacy ci-dessous.
            logger.warn(
                LOGS.ContactManager,
                `resolveContacts: RAG indisponible pour "${searchString}" — repli legacy (${error instanceof Error ? error.message : String(error)})`,
            );
        }

        // Repli legacy — best-effort, ne rejette JAMAIS : un échec renvoie un résultat vide, et le site
        // restaure le terme brut via sa substitution.
        try {
            return {
                emails: await this._searchLegacy(searchString),
                source: "legacy",
            };
        } catch (error) {
            logger.warn(
                LOGS.ContactManager,
                `resolveContacts: recherche legacy en échec pour "${searchString}" — résultat vide (${error instanceof Error ? error.message : String(error)})`,
            );
            return { emails: [], source: "legacy" };
        }
    }

    /**
     * F-1.13 — UN contact par nom, pour l'assistant (docs/refonte-assistant/CONCEPTION-contacts.md
     * de l'Api-Ai). Ne rejette jamais.
     * ═══════════════════════════════════════════════════════════════════════════
     *  1. une adresse mail est rendue telle quelle ;
     *  2. le RAG de contacts en priorité, attendu jusqu'à `ASSISTANT_RAG_TIMEOUT_MS` : s'il rend des
     *     contacts dont le nom correspond (`matchesName`), on décide avec eux seuls ;
     *  3. sinon (vide, rien de compatible, erreur, délai : la raison part au journal) le carnet de la
     *     boîte (Microsoft ou Google), AVEC le nom affiché de chaque contact, et les participants des
     *     mails chargés, sous `ASSISTANT_BOOK_TIMEOUT_MS` ;
     *  4. la même règle de décision pour les deux (`decide`) : un candidat, ou plusieurs qui se valent
     *     (`ambiguous` : l'assistant pose la question « Lequel ? ») ;
     *  5. rien de compatible : `emails` vide, avec les contacts qui ressemblent au nom (`suggestions`) ;
     *     et si ni le RAG ni le carnet n'ont pu chercher, `unavailable` (jamais « introuvable »).
     */
    public async resolveBestContact(
        searchString: string,
        opts?: ResolveContactsOptions,
    ): Promise<BestContactResult> {
        const query = searchString.trim();
        const none: BestContactResult = {
            emails: [],
            source: "legacy",
            candidates: [],
            ambiguous: false,
        };
        if (isEmailAddress(query)) {
            // Écrite par l'utilisateur : rendue telle quelle, sans filtre (`source` « rag »).
            return { ...none, emails: [query.toLowerCase()], source: "rag" };
        }
        const wanted = nameTokens(query);
        if (wanted.length === 0) return none;

        // Le carnet part si le RAG tarde ; le RAG décide toujours en premier.
        let bookSearch: ReturnType<ContactManager["_bookPeople"]> | undefined;
        const startBook = () =>
            (bookSearch ??= this._bookPeople(query, wanted));
        const headStart = setTimeout(startBook, ASSISTANT_BOOK_HEAD_START_MS);
        let rag: Awaited<ReturnType<ContactManager["_ragPeople"]>>;
        try {
            rag = await this._ragPeople(query, wanted, opts);
        } finally {
            clearTimeout(headStart);
        }
        if (rag.candidates.length > 0) {
            return this._decide(wanted, rag.candidates, "rag");
        }
        logger.warn(
            LOGS.ContactManager,
            `resolveBestContact("${query}") : RAG ${rag.reason}, repli sur le carnet de la boîte`,
        );

        const book = await startBook();
        if (book.candidates.length > 0) {
            return this._decide(wanted, book.candidates, "legacy");
        }
        // Ni le RAG ni le carnet n'ont pu chercher : ce n'est pas un nom introuvable.
        if (!rag.searched && !book.searched) {
            return { ...none, unavailable: true };
        }
        const suggestions = [...rag.suggestions, ...book.suggestions].filter(
            (candidate, index, all) =>
                all.findIndex((c) => c.email === candidate.email) === index,
        );
        return suggestions.length > 0
            ? { ...none, suggestions: suggestions.slice(0, SUGGESTIONS_MAX) }
            : none;
    }

    // #endregion Public Methods

    /**
     * La règle de décision, la même quelle que soit la source : le nom écrit mot pour mot passe devant
     * (« marc » avant « marcel ») ; dans ce groupe, un autre candidat se vaut avec le premier quand
     * leur écart de score (RAG) est sous `AMBIGUITY_GAP`, ou quand l'un d'eux n'a pas de score (carnet).
     */
    private _decide(
        wanted: string[],
        candidates: ContactCandidate[],
        source: "rag" | "legacy",
    ): BestContactResult {
        const { ranked, best: pool } = exactFirst(wanted, candidates);
        const [best, ...others] = pool;
        const ambiguous = others.some(
            (c) =>
                best.score === null ||
                c.score === null ||
                best.score - c.score < AMBIGUITY_GAP,
        );
        return { emails: [best.email], source, candidates: ranked, ambiguous };
    }

    /** Les contacts compatibles (et ceux qui ressemblent) parmi des personnes, hors no-reply, sans doublon. */
    private _compatible(
        wanted: string[],
        people: { email: string; displayName: string; score?: number | null }[],
    ): { candidates: ContactCandidate[]; suggestions: ContactCandidate[] } {
        const candidates: ContactCandidate[] = [];
        const suggestions: ContactCandidate[] = [];
        for (const person of people) {
            const email = person.email.trim().toLowerCase();
            if (!email || isNoReplyAddress(email)) continue;
            const candidate = {
                email,
                displayName: person.displayName ?? "",
                score: person.score ?? null,
            };
            if (matchesName(wanted, candidate.displayName, email)) {
                if (!candidates.some((c) => c.email === email))
                    candidates.push(candidate);
            } else if (
                resemblesName(wanted, candidate.displayName, email) &&
                !suggestions.some((c) => c.email === email)
            ) {
                suggestions.push(candidate);
            }
        }
        return { candidates, suggestions };
    }

    /**
     * Le RAG de contacts, attendu jusqu'à son délai. `searched` : il a répondu avec au moins un
     * contact (une réponse vide peut venir de son interrupteur éteint : elle ne vaut pas recherche).
     */
    private async _ragPeople(
        query: string,
        wanted: string[],
        opts?: ResolveContactsOptions,
    ): Promise<{
        candidates: ContactCandidate[];
        suggestions: ContactCandidate[];
        searched: boolean;
        reason: RagFallbackReason;
    }> {
        const empty = { candidates: [], suggestions: [], searched: false };
        try {
            const rag = await this._searchContactsWithin(
                query,
                opts?.topK ?? RAG_DEFAULT_TOP_K,
                opts?.timeoutMs ?? ASSISTANT_RAG_TIMEOUT_MS,
                "resolveBestContact",
            );
            const results = rag?.results ?? [];
            const found = this._compatible(
                wanted,
                results.map((r) => ({
                    email: r.contactEmail,
                    displayName: r.displayName,
                    score: r.score,
                })),
            );
            // Calibration du seuil de confiance (AB#1634) : les premiers candidats et leur score.
            logger.info(
                LOGS.ContactManager,
                `resolveBestContact("${query}") : ${results.length} candidats, ${found.candidates.length} compatibles`,
                results.slice(0, 5).map((r) => ({
                    email: r.contactEmail,
                    score: r.score,
                    compatible: found.candidates.some(
                        (c) => c.email === r.contactEmail.toLowerCase(),
                    ),
                })),
            );
            return {
                ...found,
                searched: results.length > 0,
                reason: results.length === 0 ? "vide" : "aucun_compatible",
            };
        } catch (error) {
            const message =
                error instanceof Error ? error.message : String(error);
            logger.warn(
                LOGS.ContactManager,
                `resolveBestContact: RAG indisponible pour "${query}" (${message})`,
            );
            return {
                ...empty,
                reason:
                    error instanceof ContactTimeoutError ? "delai" : "erreur",
            };
        }
    }

    /**
     * Le carnet de la boîte (avec les noms affichés), sous son délai, et les participants des mails
     * chargés. `searched` : le carnet a répondu, ou les mails chargés ont donné un contact.
     */
    private async _bookPeople(
        query: string,
        wanted: string[],
    ): Promise<{
        candidates: ContactCandidate[];
        suggestions: ContactCandidate[];
        searched: boolean;
    }> {
        let book: ContactPerson[] = [];
        let bookAnswered = false;
        try {
            book = await this._within(
                this.dataManager.searchContactPeople(query),
                ASSISTANT_BOOK_TIMEOUT_MS,
                "resolveBestContact: délai du carnet dépassé",
            );
            bookAnswered = true;
        } catch (error) {
            logger.warn(
                LOGS.ContactManager,
                `resolveBestContact: carnet de la boîte indisponible pour "${query}" (${error instanceof Error ? error.message : String(error)})`,
            );
        }
        const loaded = this.mailRecord.people();
        const found = this._compatible(wanted, [...book, ...loaded]);
        return {
            ...found,
            searched: bookAnswered || found.candidates.length > 0,
        };
    }

    /** `promise`, ou un rejet `message` au bout de `timeoutMs` ; le minuteur est toujours levé. */
    private async _within<T>(
        promise: Promise<T>,
        timeoutMs: number,
        message: string,
    ): Promise<T> {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        try {
            return await Promise.race([
                promise,
                new Promise<never>((_, reject) => {
                    timeoutId = setTimeout(
                        () => reject(new ContactTimeoutError(message)),
                        timeoutMs,
                    );
                }),
            ]);
        } finally {
            if (timeoutId !== undefined) clearTimeout(timeoutId);
        }
    }

    /**
     * Recherche RAG sous garde de délai (`Promise.race` avec un rejet différé) : au-delà de
     * `timeoutMs`, rejette avec « <caller>: délai RAG dépassé ». Le minuteur est levé dans tous les
     * cas, même quand le RAG gagne (pas de setTimeout pendant).
     */
    private async _searchContactsWithin(
        query: string,
        topK: number,
        timeoutMs: number,
        caller: string,
    ): Promise<SearchContactsResponseDTO> {
        return this._within(
            this.searchContacts(query, topK),
            timeoutMs,
            `${caller}: délai RAG dépassé`,
        );
    }
}
